import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Tag,
  Divider,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';
import authService from '../utils/auth';

const { Option } = Select;
const { TextArea } = Input;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

function AirlineManagement() {
  const [airlines, setAirlines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAirline, setEditingAirline] = useState(null);
  const [form] = Form.useForm();
  const [extractionMethod, setExtractionMethod] = useState('text');

  useEffect(() => {
    fetchAirlines();
  }, []);

  const fetchAirlines = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/airlines`, {
        headers: authService.getAuthHeaders()
      });
      if (res.data.success) {
        setAirlines(res.data.data);
      }
    } catch (error) {
      console.error('获取订票系统列表失败:', error);
      message.error(error.response?.data?.error || error.response?.data?.message || '获取订票系统列表失败');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingAirline(null);
    setExtractionMethod('text');
    form.resetFields();
    form.setFieldsValue({
      extractionMethod: 'text',
      isActive: true
    });
    setModalVisible(true);
  };

  const openEditModal = (airline) => {
    setEditingAirline(airline);
    setExtractionMethod(airline.extractionMethod);
    const rules = airline.textRules || {};
    form.setFieldsValue({
      name: airline.name,
      code: airline.code,
      extractionMethod: airline.extractionMethod,
      isActive: airline.isActive,
      ocrPrompt: airline.ocrPrompt,
      namePattern: rules.passengerName?.pattern || '',
      nameFlags: rules.passengerName?.flags || 'i',
      passportPattern: rules.passportNumber?.pattern || '',
      passportFlags: rules.passportNumber?.flags || 'i',
      ticketPattern: rules.ticketNumber?.pattern || '',
      ticketFlags: rules.ticketNumber?.flags || 'i'
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        name: values.name,
        extractionMethod: values.extractionMethod,
        isActive: values.isActive,
        ocrPrompt: values.extractionMethod === 'ocr' ? values.ocrPrompt : null,
        textRules: values.extractionMethod === 'text' ? {
          passengerName: { pattern: values.namePattern, flags: values.nameFlags || 'i' },
          passportNumber: { pattern: values.passportPattern, flags: values.passportFlags || 'i' },
          ticketNumber: { pattern: values.ticketPattern, flags: values.ticketFlags || 'i' }
        } : null
      };

      if (editingAirline) {
        await axios.put(`${API_BASE}/airlines/${editingAirline.id}`, data, {
          headers: authService.getAuthHeaders()
        });
        message.success('更新成功');
      } else {
        await axios.post(`${API_BASE}/airlines`, data, {
          headers: authService.getAuthHeaders()
        });
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchAirlines();
    } catch (error) {
      if (error.response) {
        message.error(error.response.data?.message || '操作失败');
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/airlines/${id}`, {
        headers: authService.getAuthHeaders()
      });
      message.success('删除成功');
      fetchAirlines();
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleToggleActive = async (airline) => {
    try {
      await axios.put(`${API_BASE}/airlines/${airline.id}`, {
        isActive: !airline.isActive
      }, {
        headers: authService.getAuthHeaders()
      });
      message.success(airline.isActive ? '已停用' : '已启用');
      fetchAirlines();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns = [
    {
      title: '系统名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '提取方式',
      dataIndex: 'extractionMethod',
      key: 'extractionMethod',
      width: 120,
      render: (method) => (
        <Tag color={method === 'text' ? 'green' : 'orange'}>
          {method === 'text' ? '文本提取' : 'OCR识别'}
        </Tag>
      )
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          size="small"
          onChange={() => handleToggleActive(record)}
        />
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除该订票系统？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title="订票系统管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            新建订票系统
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={airlines}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={editingAirline ? '编辑订票系统' : '新建订票系统'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={650}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="系统名称"
            rules={[{ required: true, message: '请输入系统名称' }]}
          >
            <Input placeholder="如: TravelSky_黑屏、TravelSky_白屏" />
          </Form.Item>

          <Form.Item
            name="extractionMethod"
            label="提取方式"
            rules={[{ required: true }]}
          >
            <Select onChange={(v) => setExtractionMethod(v)}>
              <Option value="text">文本提取（PDF文本 + 正则匹配，推荐）</Option>
              <Option value="ocr">OCR识别（GPT-4o图像识别，较慢）</Option>
            </Select>
          </Form.Item>

          <Form.Item name="isActive" label="启用状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>

          {extractionMethod === 'text' && (
            <>
              <Divider orientation="left" plain>文本提取正则规则</Divider>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                配置从PDF文本中提取字段的正则表达式，捕获组(第一个括号)为提取内容
              </Typography.Text>

              <Form.Item
                name="namePattern"
                label="乘客姓名正则"
                rules={[{ required: extractionMethod === 'text', message: '请输入姓名正则' }]}
              >
                <Input placeholder="如: NAME\s*[:：]\s*([A-Z]+/[A-Z\s]+)" />
              </Form.Item>
              <Form.Item name="nameFlags" hidden>
                <Input />
              </Form.Item>

              <Form.Item
                name="passportPattern"
                label="护照号正则"
                rules={[{ required: extractionMethod === 'text', message: '请输入护照号正则' }]}
              >
                <Input placeholder="如: ID\s*NUMBER\s*[:：]\s*PP([A-Z0-9]+)" />
              </Form.Item>
              <Form.Item name="passportFlags" hidden>
                <Input />
              </Form.Item>

              <Form.Item
                name="ticketPattern"
                label="票号正则"
                rules={[{ required: extractionMethod === 'text', message: '请输入票号正则' }]}
              >
                <Input placeholder="如: ETKT\s*NBR\s*[:：]\s*(\d{13})" />
              </Form.Item>
              <Form.Item name="ticketFlags" hidden>
                <Input />
              </Form.Item>
            </>
          )}

          {extractionMethod === 'ocr' && (
            <>
              <Divider orientation="left" plain>OCR识别配置</Divider>
              <Form.Item
                name="ocrPrompt"
                label="自定义Prompt（可选）"
              >
                <TextArea
                  rows={6}
                  placeholder="留空使用默认Prompt。自定义时请要求返回JSON格式，包含passengerName、passportNumber、ticketNumber字段。"
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default AirlineManagement;
